import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Fab,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { BRAND } from '../../components/auth/authTheme';
import ProtectedImage from '../../components/ProtectedImage';

const NAVY = BRAND.navy;
const ORANGE = BRAND.orange;
const BG = '#F8FAFC';

const CompanyCard = ({ company, handleView, handleEdit, handleDelete, canEdit, canDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const companyName = company.company_name || 'N/A';
  const location = company.location || 'N/A';
  const empStrength = company.employee_strength || '0';

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '16px',
        p: 2,
        mb: 2,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
        border: `1px solid ${BRAND.border}`,
        position: 'relative',
      }}
    >
      <Box sx={{ width: 48, height: 48, borderRadius: '12px', overflow: 'hidden', flexShrink: 0, bgcolor: BRAND.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {company.company_logo ? (
          <ProtectedImage 
            path={company.company_logo} 
            alt="Logo" 
            className="company-logo" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          />
        ) : (
          <BusinessIcon sx={{ color: BRAND.muted }} />
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>
        <Typography 
          sx={{ 
            fontSize: '1rem', 
            fontWeight: 700, 
            color: NAVY,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {companyName}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <LocationIcon sx={{ fontSize: '1rem', color: BRAND.muted }} />
          <Typography sx={{ fontSize: '0.8rem', color: BRAND.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {location}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PeopleIcon sx={{ fontSize: '1rem', color: BRAND.muted }} />
          <Typography sx={{ fontSize: '0.8rem', color: BRAND.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {empStrength} Employees
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton size="small" onClick={handleMenuOpen} sx={{ color: BRAND.muted }}>
          <MoreVertIcon />
        </IconButton>
        
        <Menu 
          anchorEl={anchorEl} 
          open={Boolean(anchorEl)} 
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          MenuListProps={{
            sx: { display: 'flex', flexDirection: 'row', p: 0.5, gap: 0.5 }
          }}
          PaperProps={{
            elevation: 4,
            sx: {
              minWidth: 'auto',
              borderRadius: '24px',
              mt: 0.5,
            }
          }}
        >
          <MenuItem onClick={() => { handleMenuClose(); handleView(company); }} sx={{ p: 1, minHeight: 'auto', borderRadius: '50%' }}>
            <VisibilityIcon fontSize="small" sx={{ color: '#2196f3' }} />
          </MenuItem>
          {canEdit && (
            <MenuItem onClick={() => { handleMenuClose(); handleEdit(company); }} sx={{ p: 1, minHeight: 'auto', borderRadius: '50%' }}>
              <EditIcon fontSize="small" sx={{ color: '#4caf50' }} />
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={() => { handleMenuClose(); handleDelete(company.id); }} sx={{ p: 1, minHeight: 'auto', borderRadius: '50%' }}>
              <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
            </MenuItem>
          )}
        </Menu>
      </Box>
    </Box>
  );
};

const CompanyMobileView = ({
  companies,
  searchTerm,
  setSearchTerm,
  handleView,
  handleEdit,
  handleDelete,
  openModalForCreate,
  canCreate,
  canEdit,
  canDelete,
}) => {
  return (
    <Box sx={{ pb: 8, pt: 1, px: 2, bgcolor: BG, minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
         <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: NAVY }}>
           Companies
         </Typography>
         <Typography sx={{ fontSize: '0.85rem', color: BRAND.muted }}>
           Manage registered organizations
         </Typography>
      </Box>

      <TextField
        fullWidth
        placeholder="Search companies..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 2.5,
          '& .MuiOutlinedInput-root': {
            bgcolor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: 'transparent' },
            '&.Mui-focused fieldset': { borderColor: ORANGE, borderWidth: '1px' },
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: BRAND.muted }} />
            </InputAdornment>
          )
        }}
      />

      <Box sx={{ mt: 1 }}>
        {companies.length > 0 ? (
          companies.map((company) => (
             <CompanyCard 
               key={company.id}
               company={company}
               handleView={handleView}
               handleEdit={handleEdit}
               handleDelete={handleDelete}
               canEdit={canEdit}
               canDelete={canDelete}
             />
          ))
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
             <Typography sx={{ color: BRAND.muted, fontWeight: 500 }}>
               No companies found
             </Typography>
          </Box>
        )}
      </Box>

      {canCreate && (
        <Fab 
          color="primary" 
          aria-label="add" 
          onClick={openModalForCreate}
          size="small"
          sx={{
            position: 'fixed',
            bottom: 'calc(90px + env(safe-area-inset-bottom, 0px))', // Above bottom nav
            right: 20,
            bgcolor: ORANGE,
            '&:hover': { bgcolor: BRAND.orangeHover },
            boxShadow: '0 4px 16px rgba(204,85,0,0.4)',
            zIndex: 1002,
          }}
        >
          <AddIcon sx={{ fontSize: '1.25rem' }} />
        </Fab>
      )}
    </Box>
  );
};

export default CompanyMobileView;
